// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useOrganization from "@/hooks/useOrganization";
import useTranslation from "@/hooks/useTranslation";
import { readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import type { TyClient, TyMembership } from "@/types";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * Between signing in and the dashboard. It loads the account and its organizations, and skips itself
 * when there is only one to choose.
 */
export default function SelectOrganizationScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const token = useAuth((state) => state.token);
    const memberships = useAuth((state) => state.memberships);
    const setSession = useAuth((state) => state.setSession);
    const setOrganization = useOrganization((state) => state.setOrganization);

    const { data, loading, error } = useFetch<{ client: TyClient; organizations: TyMembership[] }>("/auth/me");

    useEffect(() => {
        if (!data) return;
        setSession(data.client, data.organizations);
        if (data.organizations.length === 1) {
            setOrganization(data.organizations[0].organization._id, data.organizations[0].role);
            navigate("/", { replace: true });
        }
    }, [data, setSession, setOrganization, navigate]);

    if (!token) return <Navigate to="/login" replace />;

    return (
        <main className="select-org">
            <section className="select-org__card">
                <h1 className="select-org__title">{t.auth.selectOrganization}</h1>
                <p className="select-org__subtitle">{t.auth.selectOrganizationHint}</p>

                {loading && <p className="select-org__state">{t.common.loading}</p>}
                {error && <p className="select-org__state select-org__state--error">{error}</p>}
                {!loading && !error && memberships.length === 0 && <p className="select-org__state">{t.auth.noOrganizations}</p>}

                <ul className="select-org__list">
                    {memberships.map((membership) => (
                        <li key={membership.organization._id}>
                            <button
                                type="button"
                                className="select-org__item"
                                onClick={() => {
                                    setOrganization(membership.organization._id, membership.role);
                                    navigate("/", { replace: true });
                                }}
                            >
                                <Avatar name={readLangValue(membership.organization.name, lang)} picture={membership.organization.logo} size={34} />
                                <span className="select-org__item-name">{readLangValue(membership.organization.name, lang)}</span>
                                <span className="select-org__item-role">{membership.role}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            </section>
        </main>
    );
}
